import { useEffect, useMemo, useState } from 'react';
import type { CookLog, CookLogSocial, GetRecipeResponse as RecipeDetailData, Id, Ingredient, ReactionKind, RecipeStatus } from '@mlc/shared';
import {
  canEdit,
  convertToSystem,
  estimateGrams,
  dureeSecondesAffichee,
  temperatureAffichee,
  formatCourse,
  formatCuisine,
  formatDifficulty,
  formatDuration,
  formatDureeSecondes,
  formatTemperatureDEtape,
  formatRelativeDate,
  formatServings,
  formatTotalDuration,
  joinQuantityUnit,
  provenanceDeRecette,
  substitutionsFor,
  type UnitSystem,
} from '@mlc/shared';
import { adaptRecipeQuantities } from '@mlc/server/src/ingredients/adaptQuantities';
import type { AdaptedQuantity } from '@mlc/server/src/ingredients/adaptQuantities';
import { addComment, addRecipeToShopping, ApiError, archiveRecipe, deleteComment, getRecipe, listRecipeCollections, OfflineQueuedError, restoreRecipe, setReaction, updateCookLogShared, updateRecipeNote, updateRecipeRangement, updateRecipeTags } from './api';
import { readStoredTargetServings, writeStoredTargetServings } from './servingsStorage';
import { StepText } from './StepText';
import { RecipeTabs, ongletDOuverture } from './RecipeTabs';
import { SuggestionPanel } from './SuggestionPanel';
import { readUnitSystem } from './unitSystem';
import { CookMode } from './CookMode';
import { SuppressionDefinitive } from './SuppressionDefinitive';
import { unpinRecipe } from './activeRecipes';
import { CookLogForm } from './CookLogForm';
import { TagsEditor } from './TagsEditor';
import { SharePanel } from './SharePanel';
import { VisibilityToggle } from './VisibilityToggle';
import { useSession } from './session';
import { copyRecipe } from './api';
import { Link, navigate, paths, withSearchParam } from './router';
import { Banner, Button, CollapsiblePanel, EmptyState, Icon, LinkButton, Loading, TextInput, buttonClasses, cardClasses, fieldClasses } from './ui';

/**
 * Fiche recette en lecture (US-14).
 *
 * Jusqu'ici, cliquer sur une recette ouvrait `RecipeDetail` : un formulaire
 * d'édition intégral, où le titre était un `<input>` surmonté du label
 * « Titre », où les ingrédients étaient huit lignes de six contrôles et les
 * étapes des `<textarea>` flanqués de « Durée », « Température », « Groupe »
 * et d'une case « passive ». 155 éléments interactifs, dont 126 sous 40 px.
 * C'est ce qui faisait dire « prototype » en trente secondes à un observateur
 * extérieur (audit externe, constats 01 et 08).
 *
 * Une recette se consulte cent fois pour une modification : l'écran par défaut
 * est donc une page de lecture, **sans un seul champ de formulaire**, et
 * l'édition devient un écran à part (`/recette/:id/modifier`, US-15).
 *
 * Ce composant est écrit à neuf plutôt qu'obtenu en retirant des morceaux à
 * `RecipeDetail` : les deux écrans n'ont pas la même structure, et un
 * composant unique paramétré par un booléen `readOnly` aurait gardé la
 * densité de formulaire dans son arborescence. Il en résulte une duplication
 * temporaire (historique du journal, adaptation des portions) que US-15
 * résorbe en réduisant `RecipeDetail` à l'édition pure.
 */

export interface RecipeReadProps {
  recipeId: string;
  /** Mode cuisine ouvert — porté par la route depuis US-13. */
  cooking: boolean;
  onCookingChange: (cooking: boolean) => void;
}

// ----------------------------------------------------------------------------
// Mémoire du journal — la promesse du produit, remontée sous le titre
// ----------------------------------------------------------------------------

/**
 * Frise des réalisations. Elle vivait tout en bas de la fiche, sous « Notes
 * personnelles », après quatre écrans de formulaire (audit externe,
 * constat 39) — c'est-à-dire à l'endroit le moins consulté de l'écran le
 * moins lisible, alors que `cook_log` est décrit au §2 de CLAUDE.md comme
 * « la table qui porte la différenciation ». Elle passe au-dessus de la ligne
 * de flottaison.
 */
function MemoryStrip({
  cookLogs,
  now,
  recipeId,
  memberId,
}: {
  cookLogs: CookLog[];
  now: number;
  recipeId: string;
  /** Identifiant du membre connecté : **jamais son nom** (US-52). Deux Claire ne doivent pas se compter l'une l'autre. */
  memberId: string | null;
}): React.JSX.Element | null {
  const latest = cookLogs[0];
  if (latest === undefined) return null;

  // **Collective depuis US-65.** Elle disait « Vos notes : 3 → 4 → 5 » alors
  // que la fiche porte désormais les réalisations partagées des autres :
  // compter les leurs comme les siennes serait faux, et les taire viderait la
  // story de son sens. On dit les deux, dans cet ordre — la famille d'abord,
  // parce que c'est ce que la fiche montre.
  const miennes = cookLogs.filter((log) => log.member_id === memberId);
  const ratings = miennes.map((log) => log.rating).filter((r): r is NonNullable<typeof r> => r !== null);
  const count = cookLogs.length;
  const collective = miennes.length !== count;

  return (
    <div
      data-testid="memory-strip"
      className={cardClasses({ padded: false, className: 'flex flex-col gap-1 px-4 py-3' })}
    >
      <p className="text-sm text-foreground">
        <span className="font-medium">
          {count === 1 ? 'Faite une fois' : `Faite ${count} fois`}
          {collective && ` dans la famille, ${miennes.length === 0 ? 'jamais' : `${String(miennes.length)} fois`} par vous`}
        </span>
        {' · dernière '}
        <span className="text-data">{formatRelativeDate(latest.cooked_at, now)}</span>
        {latest.member_id !== memberId && latest.member_name != null && ` par ${latest.member_name}`}
        {latest.rating !== null && <span className="text-data"> · {latest.rating}/5</span>}
      </p>
      {ratings.length > 1 && (
        <p className="text-meta">
          {/* De la plus ancienne à la plus récente : la frise raconte une
              progression, et c'est ce qu'aucune autre application ne montre
              (US-30). */}
          Vos notes : <span className="text-data">{[...ratings].reverse().join(' → ')}</span>
        </p>
      )}
      {latest.next_time !== null && (
        <p className="text-sm text-foreground-secondary">
          <span className="text-foreground-muted">La prochaine fois — </span>
          {latest.next_time}
        </p>
      )}
      {/* Passerelle vers le journal transversal (US-30) : la mémoire d'une
          recette n'est qu'une tranche de celle de la cuisine entière. */}
      <Link
        to={withSearchParam(paths.journal, 'recette', recipeId)}
        className="text-meta underline"
      >
        Voir dans le journal
      </Link>
    </div>
  );
}

/**
 * Commentaires et réactions d'une réalisation (US-66).
 *
 * Le fil vit **sous** la réalisation qu'il commente, jamais sous la recette :
 * la demande porte sur ce que les autres ont *fait*.
 *
 * L'état est local et repart de ce que le serveur renvoie à chaque écriture —
 * les trois routes répondent le fil à jour, ce qui évite de recharger la fiche
 * entière pour un « bravo ».
 */
function FilDeRealisation({ log, moi }: { log: CookLog; moi: string | null }): React.JSX.Element | null {
  const [social, setSocial] = useState<CookLogSocial | null>(log.social ?? null);
  const [brouillon, setBrouillon] = useState('');
  const [ouvert, setOuvert] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  if (social === null) return null;

  function appliquer(promesse: Promise<CookLogSocial>): void {
    setErreur(null);
    promesse
      // Forme vérifiée : une réponse doublée en test — ou tronquée par un
      // filet réseau — ne doit pas faire tomber la fiche entière.
      .then((frais) => {
        setSocial({
          comments: Array.isArray(frais.comments) ? frais.comments : [],
          reactions: frais.reactions ?? {},
          my_reaction: frais.my_reaction ?? null,
        });
        setBrouillon('');
      })
      .catch((e: unknown) => { setErreur(e instanceof Error ? e.message : String(e)); });
  }

  const visibles = social.comments;

  return (
    <div className="flex flex-col gap-2 border-t border-border pt-2">
      {erreur !== null && <Banner tone="danger">{erreur}</Banner>}

      {/* Quatre réactions, dessinées (US-66) : pas d'emoji, dont le dessin
          change d'un système à l'autre. Recliquer la sienne l'annule. */}
      <div role="group" aria-label="Réagir à cette réalisation" className="flex flex-wrap gap-1">
        {REACTIONS.map(({ kind, label }) => {
          const compte = social.reactions[kind] ?? 0;
          const mienne = social.my_reaction === kind;
          return (
            <Button
              key={kind}
              type="button"
              variant={mienne ? 'primary' : 'ghost'}
              size="compact"
              aria-pressed={mienne}
              aria-label={label}
              onClick={() => { appliquer(setReaction(log.id, mienne ? null : kind)); }}
            >
              <Icon name={kind} />
              {compte > 0 && <span className="text-data">{compte}</span>}
            </Button>
          );
        })}
      </div>

      {visibles.length > 0 && (
        <ul className="flex flex-col gap-1">
          {visibles.map((commentaire) => (
            <li key={commentaire.id} className="text-sm">
              <span className="font-medium">{commentaire.member_id === moi ? 'Vous' : commentaire.member_name}</span>
              {' — '}
              {commentaire.body === null
                ? <span className="text-foreground-muted">commentaire supprimé</span>
                : commentaire.body}
              {commentaire.can_delete && (
                <Button
                  type="button"
                  variant="ghost"
                  size="compact"
                  onClick={() => { appliquer(deleteComment(log.id, commentaire.id)); }}
                >
                  Supprimer
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {ouvert ? (
        <div className="flex flex-wrap items-end gap-2">
          <label htmlFor={`commentaire-${log.id}`} className="sr-only">Votre commentaire</label>
          <TextInput
            id={`commentaire-${log.id}`}
            value={brouillon}
            onChange={(e) => { setBrouillon(e.target.value); }}
            onKeyDown={(e) => { if (e.key === 'Enter' && brouillon.trim() !== '') appliquer(addComment(log.id, brouillon)); }}
            className="flex-1"
          />
          <Button
            type="button"
            variant="secondary"
            size="compact"
            disabled={brouillon.trim() === ''}
            onClick={() => { appliquer(addComment(log.id, brouillon)); }}
          >
            Envoyer
          </Button>
        </div>
      ) : (
        <Button type="button" variant="ghost" size="compact" className="w-fit" onClick={() => { setOuvert(true); }}>
          Commenter
        </Button>
      )}
    </div>
  );
}

/** Les quatre réactions, avec ce qu'un lecteur d'écran doit entendre. */
const REACTIONS: readonly { kind: ReactionKind; label: string }[] = [
  { kind: 'bravo', label: 'Bravo' },
  { kind: 'envie', label: 'Ça donne envie' },
  { kind: 'question', label: 'Une question' },
  { kind: 'refaire', label: 'À refaire' },
];

/**
 * Les réalisations de cette recette (US-11, refondu en US-65).
 *
 * ### Ce que c'était
 *
 * Un panneau **replié**, intitulé « Historique des réalisations », où la photo
 * mesurait 96 px de côté, portait `alt=""`, n'était pas cliquable, et où tout
 * était à la première personne du singulier. C'est pourtant la seule chose que
 * cette application a et qu'aucune autre n'a.
 *
 * ### Ce que c'est
 *
 * Une section **dépliée**, où la photo est la vedette : pleine largeur de sa
 * carte, avec un `alt` qui dit qui a fait quoi. Chaque réalisation porte le
 * prénom de son auteur, sa date, sa note et ce qui s'est passé.
 *
 * `next_time` n'y figure que pour les siennes — et ce n'est pas l'écran qui le
 * décide : le serveur ne l'envoie pas (US-65). C'est un mot qu'on s'écrit à
 * soi-même.
 */
function RealisationsPanel({ cookLogs, now, memberId, onPartage }: {
  cookLogs: CookLog[];
  now: number;
  memberId: string | null;
  /** Publier ou reprendre une de ses réalisations. Absent quand la recette n'est pas dans le livre de famille. */
  onPartage: ((log: CookLog, shared: boolean) => void) | null;
}): React.JSX.Element | null {
  if (cookLogs.length === 0) return null;

  return (
    <section aria-labelledby="titre-realisations" className="flex flex-col gap-3">
      <h2 id="titre-realisations" className="text-heading-2">
        Réalisations <span className="text-data text-base font-normal">({cookLogs.length})</span>
      </h2>
      <ul data-testid="cook-log-history" className="flex flex-col gap-4 sm:grid sm:grid-cols-2">
        {cookLogs.map((log) => {
          const mienne = log.member_id === memberId;
          const auteur = mienne ? 'Vous' : (log.member_name ?? 'La maison');
          return (
            <li key={log.id} className={cardClasses({ className: 'flex flex-col gap-2' })}>
              {log.photo_path !== null && (
                <img
                  data-testid={`cook-log-photo-${log.id}`}
                  src={`/api/photos/${log.photo_path}`}
                  alt={`Réalisation ${auteur === 'Vous' ? 'la vôtre' : `de ${auteur}`}, ${formatRelativeDate(log.cooked_at, now)}`}
                  className="aspect-[4/3] w-full rounded-card object-cover"
                />
              )}
              <p className="text-sm font-medium">
                {auteur}
                {' · '}
                <span className="text-data">{formatRelativeDate(log.cooked_at, now)}</span>
                {log.rating !== null && <span className="text-data"> · {log.rating}/5</span>}
                {log.actual_minutes !== null && (
                  <span className="text-data"> · {formatDuration(log.actual_minutes) ?? ''}</span>
                )}
              </p>
              {log.what_happened !== null && <p className="text-sm">{log.what_happened}</p>}
              {/* Seulement les siennes : le serveur ne renvoie pas celui des
                  autres, et l'afficher supposerait qu'il l'ait envoyé. */}
              {log.next_time !== null && (
                <p className="text-sm text-foreground-secondary">À retenir : {log.next_time}</p>
              )}
              {/* Le fil et les réactions (US-66) : une réalisation visible
                  sans moyen de répondre est une vitrine, pas une
                  conversation. */}
              <FilDeRealisation log={log} moi={memberId} />

              {mienne && onPartage !== null && (
                <Button
                  type="button"
                  variant={log.shared === 1 ? 'ghost' : 'secondary'}
                  size="compact"
                  aria-pressed={log.shared === 1}
                  onClick={() => { onPartage(log, log.shared !== 1); }}
                  className="w-fit"
                >
                  {log.shared === 1 ? 'Visible de la famille' : 'Gardée pour moi'}
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ----------------------------------------------------------------------------
// Ingrédients
// ----------------------------------------------------------------------------

/**
 * Case de préparation. Un `<button role="checkbox">` de 48 px et non une case
 * native de 13 px (§4 : « aucune cible tactile inférieure à 48 px »). L'état
 * n'est pas persisté : cocher en lisant sa liste avant de commencer est un
 * geste de l'instant, pas une donnée de la recette.
 */
function PrepCheckbox({
  checked,
  onToggle,
  label,
}: {
  checked: boolean;
  onToggle: () => void;
  label: string;
}): React.JSX.Element {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      onClick={onToggle}
      // La cible fait 48 px (§4), la marque visuelle 20 px : une liste
      // d'ingrédients ne doit pas être dominée par ses cases à cocher. Le
      // bouton reste transparent, seule la petite case porte la bordure.
      className="group flex size-touch shrink-0 items-center justify-center rounded-control transition-colors"
    >
      <span
        aria-hidden="true"
        className={`flex size-5 items-center justify-center rounded-[0.25rem] border text-xs leading-none transition-colors ${
          checked
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-border-strong text-transparent group-hover:border-foreground-muted'
        }`}
      >
        {/* Coche dessinée et non le caractère « ✓ » : §4 CLAUDE.md le
            proscrit, et `ui/Checkbox.tsx` le fait déjà depuis US-25
            « parce qu'une police peut ne pas l'avoir ». Cette case-ci,
            réécrite localement pour porter une marque de 20 px dans une
            cible de 48, était restée en arrière. */}
        <Icon name="coche" size={0.85} />
      </span>
    </button>
  );
}

function groupByLabel<T extends { group_label: string | null }>(rows: T[]): { label: string | null; items: T[] }[] {
  const groups: { label: string | null; items: T[] }[] = [];
  for (const row of rows) {
    const last = groups[groups.length - 1];
    if (last !== undefined && last.label === row.group_label) last.items.push(row);
    else groups.push({ label: row.group_label, items: [row] });
  }
  return groups;
}

/**
 * Liste des étapes, découpée en phases (US-41).
 *
 * Le titre de phase était rendu **à l'intérieur** du `<li>`, juste à droite du
 * numéro : « Pour le craquelin » se lisait alors comme une légende de l'étape 1
 * plutôt que comme l'ouverture d'un chapitre. La fiche CookBook (relue le
 * 10 août 2026) tranche autrement — chaque groupe d'ingrédients y est un bloc
 * distinct dont le titre a le **poids d'un titre de section**, pas d'une
 * étiquette. C'est cette hiérarchie qui est reprise ici :
 *
 * - le titre sort de la liste numérotée et occupe toute la largeur ;
 * - il est au même niveau typographique que « Étapes », prolongé d'un filet
 *   qui matérialise la rupture ;
 * - le numéro d'étape devient une pastille ronde, si bien qu'un titre et une
 *   étape ne peuvent plus se confondre, même lus en diagonale.
 *
 * La numérotation reste **continue** d'une phase à l'autre : c'est le déroulé
 * d'une seule recette, pas quatre recettes juxtaposées.
 */
function StepList({
  steps,
  ingredientsDeLEtape,
}: {
  steps: RecipeDetailData['steps'];
  ingredientsDeLEtape: (stepId: string) => string[];
}): React.ReactElement {
  // Numéro calculé **avant** le regroupement : le découpage en phases ne doit
  // rien changer au déroulé, et un compteur incrémenté pendant le rendu serait
  // une mutation en cours de rendu (interdite par react-hooks/immutability).
  const numerotees = steps.map((step, index) => ({ ...step, numero: index + 1 }));
  const groupes = groupByLabel(numerotees);

  return (
    <div data-testid="steps" className="flex flex-col gap-8">
      {groupes.map((groupe, groupeIndex) => {
        return (
          <div key={groupe.label ?? `phase-${String(groupeIndex)}`} className="flex flex-col gap-4">
            {groupe.label !== null && (
              <h3 className="flex items-center gap-3">
                <span className="text-heading-2">{groupe.label}</span>
                <span aria-hidden="true" className="h-px flex-1 bg-border" />
              </h3>
            )}
            <ol start={groupe.items[0]?.numero ?? 1} className="flex flex-col gap-6">
              {groupe.items.map((step) => {
                // Même priorité qu'en mode cuisson (US-17) : la valeur saisie
                // prime, l'extraction ne comble qu'un champ vide — et jamais
                // un champ vidé à la main (US-78, D11) : le gel vaut aussi
                // pour l'affichage, sinon la valeur effacée réapparaît ici
                // à chaque rendu.
                const duration = formatDureeSecondes(dureeSecondesAffichee(step));
                // Le thermostat n'a de sens que si l'étape parle d'un four (US-100).
                const temperature = formatTemperatureDEtape(temperatureAffichee(step), step.instruction);
                const extras = [duration, temperature].filter((p): p is string => p !== null);
                return (
                  <li key={step.id} className="flex gap-4">
                    <span
                      aria-hidden="true"
                      className="text-data flex size-8 shrink-0 items-center justify-center rounded-full bg-surface-raised text-sm ring-1 ring-border-strong ring-inset text-foreground-secondary"
                    >
                      {step.numero}
                    </span>
                    <div className="flex min-w-0 flex-col gap-1 pt-0.5">
                      <StepText
                        instruction={step.instruction}
                        ingredients={ingredientsDeLEtape(step.id)}
                        className="text-step text-foreground"
                      />
                      {extras.length > 0 && (
                        <p className="text-data text-sm text-foreground-muted">{extras.join(' · ')}</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Ce qui remplace une section « Étapes » vide (US-74).
 *
 * La moitié des recettes qui arrivent par le sas n'ont pas d'étapes : une
 * légende de Reel donne quoi acheter, pas comment faire (§C4 du plan de vague
 * G). Ce n'est pas un défaut de l'extraction, c'est la nature du format — et
 * une fiche qui affiche un intitulé « Étapes » suivi de rien se lit comme une
 * fiche cassée, ce que le §4 nomme déjà : « un intitulé suivi de rien est un
 * défaut, pas une information ».
 *
 * **On ne devine jamais le procédé** (D6). L'application dit où il se trouve et
 * y mène ; inventer « Mélangez, enfournez 25 min » serait l'« import
 * silencieusement faux » du brief §8, dans sa forme la plus tentante.
 *
 * Deux états vides, et la distinction est le cœur de la story :
 *
 * - **avec un lien**, le procédé est *ailleurs*, et on peut y aller ;
 * - **sans lien** — une saisie manuelle qu'on n'a pas finie — il n'est nulle
 *   part, et le seul geste utile est de l'écrire.
 *
 * Les deux passent par `EmptyState` (US-20) plutôt que par un bloc dessiné à
 * part : c'est la primitive de cette situation, et le §4 impose de l'utiliser
 * dès qu'elle existe. Ce qui change, ce sont les mots et l'issue.
 */
function ProcedeAbsent({
  recipe,
  modifiable,
}: {
  recipe: Pick<RecipeDetailData, 'id' | 'source_name' | 'source_url'>;
  modifiable: boolean;
}): React.JSX.Element {
  const provenance = provenanceDeRecette(recipe);
  // « Écrire les étapes » ouvre l'édition **sur les étapes** : le paramètre est
  // lu par `RecipeDetail`, qui met la section au premier plan. Sans lui, on
  // atterrit en haut d'un écran de 2 000 px.
  const ecrireLesEtapes = modifiable
    ? (
      <Link
        to={withSearchParam(paths.editRecipe(recipe.id), 'section', 'etapes')}
        className={buttonClasses(recipe.source_url === null ? 'primary' : 'secondary', 'default', 'no-underline')}
      >
        Écrire les étapes
      </Link>
      )
    : null;

  if (recipe.source_url === null) {
    return (
      <EmptyState
        title="Aucune étape enregistrée"
        description={
          modifiable
            ? 'Rien n’a été écrit pour l’instant, et rien n’a été deviné.'
            : 'Rien n’a été écrit pour l’instant. Son propriétaire peut les ajouter.'
        }
        {...(ecrireLesEtapes === null ? {} : { action: ecrireLesEtapes })}
      />
    );
  }

  return (
    <EmptyState
      title={provenance?.video === true ? 'Le procédé est dans la vidéo' : 'Le procédé est sur la page d’origine'}
      description={
        provenance?.video === true
          ? `Les ingrédients ont été relevés, les gestes non : ${provenance.nom} les montre sans les écrire. Rien n’a été inventé pour combler le vide.`
          : 'Les ingrédients ont été relevés, les gestes non. Rien n’a été inventé pour combler le vide.'
      }
      action={(
        <div className="flex flex-wrap items-center justify-center gap-3">
          {/* Vrai lien externe, nouvel onglet : le mode cuisine et ses
              minuteurs survivent au détour par la vidéo (`activeRecipes.ts`
              gère déjà ce cas), ce qu'une navigation dans le même onglet ne
              garantirait pas. `<a>` et non `Link` — le routeur ne connaît que
              les chemins de l'application. */}
          <LinkButton
            variant="primary"
            href={recipe.source_url}
            target="_blank"
            rel="noreferrer noopener"
            className="no-underline"
          >
            {provenance === null ? 'Ouvrir la page d’origine' : `Revoir sur ${provenance.nom}`}
          </LinkButton>
          {ecrireLesEtapes}
        </div>
      )}
    />
  );
}

/**
 * Quantité telle qu'elle doit s'afficher, dans le système choisi (US-34).
 * Renvoie la valeur d'origine dès que la conversion n'a pas de sens — une
 * gousse d'ail ne se convertit pas.
 */
function displayQuantity(
  quantity: number | null,
  unit: Ingredient['unit'],
  system: UnitSystem,
): { quantity: number | null; unit: Ingredient['unit']; display: string | null } {
  if (quantity === null || unit === null) return { quantity, unit, display: null };
  const converted = convertToSystem(quantity, unit, system);
  if (converted === null || converted.unit === unit) return { quantity, unit, display: null };
  // Deux chiffres significatifs, comme l'adaptation des quantités (§3.1) :
  // « 8,46 oz » n'aide personne.
  const rounded = Math.round(converted.quantity * 100) / 100;
  return { quantity: rounded, unit: converted.unit, display: String(rounded) };
}

function IngredientsPanel({
  recipeId,
  referenceServings,
  servingsLabel,
  ingredients,
  system,
}: {
  recipeId: string;
  referenceServings: number | null;
  servingsLabel: string | null;
  ingredients: Ingredient[];
  system: UnitSystem;
}): React.JSX.Element {
  const [target, setTarget] = useState<number | null>(
    () => readStoredTargetServings(recipeId) ?? referenceServings,
  );
  const [checked, setChecked] = useState<ReadonlySet<Id>>(() => new Set());
  // « Ajouté » et non « Ajouter » une fois fait : la confirmation est dans le
  // bouton lui-même, il n'y a pas de bandeau à chasser (US-28).
  const [addedToList, setAddedToList] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const adapted = useMemo<AdaptedQuantity[] | null>(() => {
    if (referenceServings === null || target === null) return null;
    return adaptRecipeQuantities({
      referenceServings,
      targetServings: target,
      ingredients: ingredients.map((i) => ({
        quantity: i.quantity,
        unit: i.unit,
        scaling: i.scaling,
        name: i.name,
      })),
      steps: [],
    }).ingredients;
  }, [referenceServings, target, ingredients]);

  function changeTarget(delta: number): void {
    const base = target ?? referenceServings ?? 1;
    const next = Math.max(1, base + delta);
    setTarget(next);
    writeStoredTargetServings(recipeId, next);
  }

  /**
   * Ajout à la liste de courses (US-28), **aux portions actuellement
   * choisies** : c'est le sens du geste. Les quantités sont recalculées par le
   * serveur à partir de la recette enregistrée — le client n'envoie que le
   * nombre de portions, jamais des quantités qu'il aurait calculées lui-même.
   */
  function addToShoppingList(): void {
    setListError(null);
    addRecipeToShopping(recipeId, target)
      .then(() => { setAddedToList(true); })
      .catch((e: unknown) => {
        // Hors ligne, la mutation est mise en file (US-12) : c'est un succès
        // différé, pas un échec.
        if (e instanceof OfflineQueuedError) { setAddedToList(true); return; }
        setListError(e instanceof Error ? e.message : String(e));
      });
  }

  function toggle(id: Id): void {
    setChecked((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <section aria-labelledby="titre-ingredients" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 id="titre-ingredients" className="text-heading-2">Ingrédients</h2>
        {referenceServings !== null && (
          <div className="flex items-center gap-2" role="group" aria-label="Adapter les portions">
            {/* `−` et `+` étaient des caractères : leur dessin dépendait de
                la police installée, et le moins mathématique n'a ni la même
                largeur ni la même hauteur d'un système à l'autre — les deux
                boutons du sélecteur de portions ne se ressemblaient pas. */}
            <Button type="button" variant="secondary" size="icon" aria-label="Une portion de moins" onClick={() => { changeTarget(-1); }}>
              <Icon name="moins" />
            </Button>
            <span data-testid="target-servings" className="text-data min-w-24 text-center text-sm">
              {formatServings(target, servingsLabel) ?? ''}
            </span>
            <Button type="button" variant="secondary" size="icon" aria-label="Une portion de plus" onClick={() => { changeTarget(1); }}>
              <Icon name="plus" />
            </Button>
            {/* Chemin de retour (US-38) : sans lui, rien ne dit quelles étaient
                les portions de la recette ni comment y revenir. Masqué quand on
                y est déjà — un bouton sans effet est une fausse promesse. */}
            {target !== referenceServings && (
              <Button
                type="button"
                variant="ghost"
                aria-label={`Revenir aux portions de la recette : ${formatServings(referenceServings, servingsLabel) ?? ''}`}
                onClick={() => { setTarget(referenceServings); writeStoredTargetServings(recipeId, referenceServings); }}
              >
                Original
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <Button type="button" variant="secondary" onClick={addToShoppingList} disabled={addedToList}>
          {addedToList ? <><Icon name="coche" /> Ajouté à la liste</> : 'Ajouter à ma liste'}
        </Button>
        {addedToList && (
          <Link to={paths.shopping} className={buttonClasses('ghost', 'inline', 'self-start')}>
            Voir ma liste de courses
          </Link>
        )}
        {listError !== null && <Banner tone="danger">{listError}</Banner>}
      </div>

      {groupByLabel(ingredients).map((group, groupIndex) => (
        <div key={group.label ?? `groupe-${String(groupIndex)}`} className="flex flex-col gap-2">
          {group.label !== null && (
            <h3 className="text-sm font-semibold uppercase tracking-wide text-foreground-muted">{group.label}</h3>
          )}
          <ul className="flex flex-col gap-1">
            {group.items.map((ingredient) => {
              const index = ingredients.indexOf(ingredient);
              const row = adapted?.[index];
              // Affichage dans le système choisi (US-34), jamais un
              // enregistrement : la recette garde les unités dans lesquelles
              // elle a été écrite, et une bascule aller-retour ne fait donc
              // dériver aucune quantité.
              const shown = displayQuantity(row?.quantity ?? ingredient.quantity, ingredient.unit, system);
              const prefix = joinQuantityUnit(
                shown.display ?? row?.display ?? (ingredient.quantity !== null ? String(ingredient.quantity) : null),
                shown.unit,
                shown.quantity,
              );
              // Estimation calculée sur la quantité **d'origine** et son unité :
              // la valeur convertie ne veut plus rien dire pour une mesure de
              // maison.
              const estimate = estimateGrams(
                row?.quantity ?? ingredient.quantity ?? 0,
                ingredient.unit ?? 'g',
                ingredient.name,
              );
              const alternatives = substitutionsFor(ingredient.name);
              const isChecked = checked.has(ingredient.id);
              return (
                <li key={ingredient.id} data-testid={`ingredient-${ingredient.id}`} className="flex items-center gap-3">
                  <PrepCheckbox
                    checked={isChecked}
                    onToggle={() => { toggle(ingredient.id); }}
                    label={`Préparé : ${ingredient.name}`}
                  />
                  <span className={isChecked ? 'text-foreground-muted line-through' : 'text-foreground'}>
                    {prefix !== '' && <span className="text-data font-semibold">{prefix} </span>}
                    {ingredient.name}
                    {ingredient.note !== null && (
                      <span className="text-foreground-muted"> — {ingredient.note}</span>
                    )}
                    {ingredient.is_optional === 1 && (
                      <span className="text-foreground-muted"> (facultatif)</span>
                    )}
                    {/* Équivalence approximative d'une mesure de maison
                        (US-34), annoncée comme telle : une cuillère à soupe de
                        farine et une de miel ne pèsent pas la même chose, et
                        donner « 10 g » sans le dire serait faux et crédible. */}
                    {estimate !== null && (
                      <span className="text-meta"> (≈ {Math.round(estimate.grams)} g)</span>
                    )}
                    {alternatives.length > 0 && (
                      <details className="mt-1 text-meta">
                        <summary className="min-h-touch-min cursor-pointer">Pas de {ingredient.name} ?</summary>
                        <ul className="mt-1 flex flex-col gap-1 pl-4">
                          {alternatives.map((option) => (
                            <li key={option.replacement}>
                              <span className="text-foreground-secondary">{option.replacement}</span> — {option.caveat}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </section>
  );
}

// ----------------------------------------------------------------------------
// Écran
// ----------------------------------------------------------------------------

/**
 * « Cette recette apparaît dans : Desserts rapides, Le dimanche » (US-63).
 *
 * Ce n'est pas un rangement, c'est une **conséquence** : une collection étant
 * une recherche sauvegardée, l'appartenance se calcule. Aucune table de liaison
 * n'a été créée, et c'est ce qui garde la promesse d'US-21 — un dossier se
 * périme dès qu'une nouvelle recette arrive, une recherche non.
 *
 * Seules **ses** collections sont interrogées, côté serveur : celles des autres
 * diraient ce qu'ils rangent. Silencieux quand il n'y en a aucune — une ligne
 * « appartient à : rien » n'apprend rien.
 */
function AppartenanceAuxCollections({ recipeId }: { recipeId: string }): React.JSX.Element | null {
  const [collections, setCollections] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    const controller = new AbortController();
    // `Promise.resolve().then(…)` : sur un `fetch` doublé qui ne connaît pas
    // cette adresse, l'appel lève **de façon synchrone** et l'exception
    // remonterait dans le rendu, vidant la fiche entière. Le même piège avait
    // déjà emporté l'autocomplétion des cuisines en US-55 ; une mention de
    // confort ne doit jamais casser l'écran qu'elle décore.
    Promise.resolve()
      .then(() => listRecipeCollections(recipeId, controller.signal))
      // Forme vérifiée : un `fetch` doublé peut renvoyer autre chose qu'un
      // tableau, et `collections.length` planterait le rendu de la fiche.
      .then((liste) => { setCollections(Array.isArray(liste) ? liste : []); })
      .catch(() => { /* silencieux : c'est du confort */ });
    return () => { controller.abort(); };
  }, [recipeId]);

  if (collections.length === 0) return null;

  return (
    <p data-testid="recipe-collections" className="text-sm text-foreground-secondary">
      Dans vos collections :{' '}
      {collections.map((collection, index) => (
        <span key={collection.id}>
          {index > 0 && ', '}
          {collection.name}
        </span>
      ))}
    </p>
  );
}

/**
 * Éditeur de note personnelle (US-64). Un `<textarea>` et un bouton : la note
 * est un texte libre, souvent multiligne — « la pâte gagne à reposer une nuit »
 * s'écrit rarement en une ligne.
 *
 * L'enregistrement est explicite. Une sauvegarde à la frappe sur un champ de
 * plusieurs lignes produit une requête par mot, et rien à l'écran ne dirait ce
 * qui est enregistré.
 */
function NoteEditor({ note, busy, onSave }: {
  note: string;
  busy: boolean;
  onSave: (valeur: string) => Promise<void>;
}): React.JSX.Element {
  const [brouillon, setBrouillon] = useState(note);
  const modifiee = brouillon.trim() !== note.trim();

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor="note-personnelle" className="sr-only">Ma note sur cette recette</label>
      <textarea
        id="note-personnelle"
        value={brouillon}
        rows={3}
        disabled={busy}
        onChange={(e) => { setBrouillon(e.target.value); }}
        placeholder="Ce que je veux me rappeler : une astuce, un ratage, une variante."
        className={`${fieldClasses()} w-full`}
      />
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="secondary" size="compact" disabled={busy || !modifiee} onClick={() => { void onSave(brouillon); }}>
          Enregistrer ma note
        </Button>
        {note !== '' && (
          <Button type="button" variant="ghost" size="compact" disabled={busy} onClick={() => { setBrouillon(''); void onSave(''); }}>
            Effacer
          </Button>
        )}
      </div>
    </div>
  );
}

export function RecipeRead({ recipeId, cooking, onCookingChange }: RecipeReadProps): React.JSX.Element {
  const [detail, setDetail] = useState<RecipeDetailData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [addingCookLog, setAddingCookLog] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [busy, setBusy] = useState(false);
  /** Incrémenté pour relire la fiche après une bascule de visibilité (US-49). */
  const [refreshToken, setRefreshToken] = useState(0);
  const session = useSession();
  /**
   * La même règle que le serveur (`shared/src/access.ts`), importée et non
   * recopiée. Tant que la session n'est pas connue, rien n'est modifiable :
   * mieux vaut un contrôle qui apparaît qu'un contrôle qui disparaît sous le
   * doigt.
   */
  const modifiable = detail !== null && session?.member != null && canEdit(session.member, detail);
  /**
   * **Ranger n'est pas modifier** (US-51) : le statut et les étiquettes
   * personnelles sont ouverts à quiconque voit la recette. Pas de `canSee` ici
   * — la fiche est chargée, donc elle est visible ; c'est le serveur qui en a
   * décidé, et le redire côté client serait une seconde définition de la règle.
   *
   * Un appareil de cuisine partagée (US-50) en est exclu : ce n'est pas un
   * membre, rien ne peut lui appartenir, et le serveur refuse ses écritures.
   */
  const rangeable = detail !== null && session?.member != null && session.device === null;
  /**
   * La suppression définitive (US-83) : **le rôle en plus de la propriété**,
   * et la recette déjà archivée (D17, D18). Les trois conditions sont celles
   * de la route, écrites ici pour ne rien afficher qui répondrait 404 —
   * promettre puis ne rien faire est le défaut qu'US-51 est allée corriger sur
   * les boutons de la recette d'un autre.
   */
  const supprimable = modifiable && detail !== null && detail.archived === 1
    && session?.member?.role === 'proprietaire';
  // Fixé au montage : une date relative qui se recalculerait à chaque rendu
  // rendrait les tests dépendants de l'horloge sans rien apporter à l'écran.
  const [now] = useState(() => Date.now());
  // Système d'unités d'affichage (US-34), lu une fois : il ne change pas en
  // cours de lecture d'une recette.
  const [system] = useState(readUnitSystem);

  /** La recette n'est plus là — supprimée, ou sortie du périmètre visible (US-83). */
  const [disparue, setDisparue] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    getRecipe(recipeId, controller.signal)
      .then(setDetail)
      .catch((e: unknown) => {
        if (e instanceof Error && e.name === 'AbortError') return;
        if (e instanceof ApiError && e.code === 'not_found') {
          setDisparue(true);
          // La barre « En cuisson » cesse d'y renvoyer : proposer de reprendre
          // une cuisson sur une recette qui n'existe plus enverrait sur ce
          // même message, indéfiniment.
          unpinRecipe(recipeId);
        }
        setLoadError(e instanceof Error ? e.message : String(e));
      });
    return () => { controller.abort(); };
  }, [recipeId, refreshToken]);

  /**
   * Statut de rangement (US-21). Deux boutons plutôt qu'un menu : ce sont deux
   * intentions distinctes, pas deux valeurs d'un même réglage, et un menu
   * déroulant demanderait deux gestes là où un seul suffit. Cliquer le statut
   * actif le retire — marquer et démarquer sont le même geste.
   */
  function toggleStatus(current: RecipeDetailData, status: RecipeStatus): void {
    setBusy(true);
    setActionError(null);
    updateRecipeRangement(current.id, current.status === status ? null : status)
      .then((updated) => { setDetail({ ...current, ...updated }); })
      .catch((e: unknown) => { setActionError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { setBusy(false); });
  }

  /**
   * Étiquettes personnelles (US-51). Même geste que le statut, et même règle :
   * la fiche renvoyée par le serveur remplace l'état local, pour que
   * l'étiquette calculée qui apparaît ou disparaît au passage se voie.
   */
  function saveTags(current: RecipeDetailData, names: string[]): Promise<void> {
    setActionError(null);
    return updateRecipeTags(current.id, names)
      .then(() => getRecipe(current.id))
      .then(setDetail)
      .catch((e: unknown) => { setActionError(e instanceof Error ? e.message : String(e)); });
  }

  /**
   * La note personnelle (US-64). Comme le rangement : la fiche renvoyée par le
   * serveur remplace l'état local, pour qu'une note effacée disparaisse sans
   * rechargement.
   */
  function saveNote(current: RecipeDetailData, note: string): Promise<void> {
    setBusy(true);
    setActionError(null);
    return updateRecipeNote(current.id, note.trim() === '' ? null : note)
      .then((updated) => { setDetail({ ...current, ...updated }); })
      .catch((e: unknown) => { setActionError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { setBusy(false); });
  }

  /**
   * Publier une de ses réalisations, ou la reprendre (US-65). La fiche
   * renvoyée par le serveur remplace l'état local : c'est lui qui décide ce
   * qui est visible, et le recopier ici ferait deux vérités.
   */
  function partagerRealisation(current: RecipeDetailData, log: CookLog, shared: boolean): void {
    setBusy(true);
    setActionError(null);
    updateCookLogShared(current.id, log.id, shared)
      .then((updated) => { setDetail({ ...current, ...updated }); })
      .catch((e: unknown) => { setActionError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { setBusy(false); });
  }

  function toggleArchive(current: RecipeDetailData): void {
    setBusy(true);
    setActionError(null);
    const action = current.archived === 1 ? restoreRecipe : archiveRecipe;
    action(current.id)
      .then((updated) => { setDetail({ ...current, ...updated }); })
      .catch((e: unknown) => { setActionError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { setBusy(false); });
  }

  if (loadError !== null) {
    return (
      <div className="flex flex-col gap-4">
        <Link to={paths.library} className={buttonClasses('quiet', 'compact', 'self-start')}><Icon name="fleche-gauche" size={0.9} /> Bibliothèque</Link>
        {/* **Le mode cuisine survit à la disparition de sa recette** (US-83).
            Elle peut être supprimée depuis un autre appareil pendant qu'on la
            cuisine ici : cet écran doit le dire, pas se casser. Une recette
            qu'on ne voit plus et une recette supprimée se répondent de la même
            façon (404) et se disent de la même façon — distinguer les deux
            apprendrait qu'elle a existé. */}
        {disparue
          ? <Banner tone="warning">Cette recette n’est plus disponible. Elle a pu être supprimée ou ne plus vous être partagée.</Banner>
          : <Banner tone="danger">Impossible de charger la recette : {loadError}</Banner>}
      </div>
    );
  }

  if (detail === null) {
    return <Loading label="la recette" />;
  }

  /**
   * Noms normalisés des ingrédients associés à une étape (US-39). Rien n'est
   * deviné : `step_ingredient` est peuplée par `matchStepIngredients` (US-08),
   * ce composant ne fait que l'exposer.
   */
  const ingredientsDeLEtape = (stepId: string): string[] =>
    detail.step_ingredients
      .filter((pair) => pair.step_id === stepId)
      .map((pair) => detail.ingredients.find((i) => i.id === pair.ingredient_id)?.name_normalized ?? '')
      .filter((nom) => nom.length > 0);

  const total = formatTotalDuration(detail);
  const meta = [
    total !== null ? `${total.label} au total` : null,
    total?.activeLabel !== null && total?.activeLabel !== undefined ? `dont ${total.activeLabel} de préparation` : null,
    formatServings(detail.servings, detail.servings_label),
    formatDifficulty(detail.difficulty),
    // Nature du plat et cuisine (US-55), jamais brutes : `course` est une
    // valeur d'énumération, et le §3.5 interdit de l'afficher telle quelle.
    formatCourse(detail.course),
    formatCuisine(detail.cuisine),
  ].filter((part): part is string => part !== null);

  return (
    <article className="flex flex-col gap-8" aria-label={`Recette : ${detail.title}`}>
      <Link to={paths.library} className={buttonClasses('quiet', 'compact', 'self-start')}><Icon name="fleche-gauche" size={0.9} /> Bibliothèque</Link>

      {actionError !== null && <Banner tone="danger">{actionError}</Banner>}
      {detail.archived === 1 && (
        <Banner tone="warning">
          Cette recette est archivée : elle n’apparaît plus dans la bibliothèque ni dans la recherche.
        </Banner>
      )}

      {/* Panneaux transitoires, hors des onglets : ils répondent à une action
          qu'on vient de faire et doivent rester visibles quel que soit l'onglet
          actif. */}
      {sharing && <SharePanel detail={detail} onClose={() => { setSharing(false); }} />}

      {/* Copier la recette d'un autre (US-49, cas 02) : « contribuer, sans
          toucher aux miennes ». Le bouton n'apparaît que sur ce qui ne nous
          appartient pas — dupliquer la sienne serait un geste sans usage.

          **La comparaison porte sur les identifiants** (US-52) : elle portait
          sur les noms affichés, ce qui faisait disparaître ce bouton dès que
          deux membres partageaient un prénom. */}
      {detail.owner_name !== null && detail.owner_id !== session?.member?.id && (
        // `rounded-card` et non `rounded-lg`, que le §4 de CLAUDE.md proscrit :
        // il fait doublon avec `rounded-control`, et ceci est un panneau.
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-border bg-surface p-4">
          <p className="max-w-reading text-sm text-foreground-secondary">
            Cette recette est celle de {detail.owner_name}. Pour l’adapter à votre façon, faites-en une copie.
          </p>
          <Button
            type="button"
            variant="secondary"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              copyRecipe(detail.id)
                .then((copie) => { navigate(paths.recipe(copie.id)); })
                .catch((e: unknown) => { setActionError(e instanceof Error ? e.message : String(e)); })
                .finally(() => { setBusy(false); });
            }}
          >
            En faire une copie
          </Button>
        </div>
      )}

      {addingCookLog && (
        <CookLogForm
          recipeId={detail.id}
          prefilledActualMinutes={null}
          dismissLabel="Annuler"
          onSaved={(updated) => { setDetail(updated); setAddingCookLog(false); }}
          onDismiss={() => { setAddingCookLog(false); }}
        />
      )}

      {/* Sous `lg` : trois onglets, un seul contenu visible, la barre en bas à
          hauteur du pouce (US-40). Au-dessus : `RecipeTabs` s'efface et cette
          grille reprend la main — les ingrédients restent visibles pendant
          qu'on lit les étapes, ce qui supprime le va-et-vient permanent.
          Un même contenu, écrit une fois, deux mises en page. */}
      {/* **Le titre appartient à la fiche, pas à l'onglet « À propos »**
          (US-58). Il vivait dans le bloc `apropos`, donc masqué sous `lg` dès
          que la fiche s'ouvrait sur « Instructions » — c'est-à-dire pour toute
          recette déjà cuisinée, la totalité du corpus importé. On lisait des
          étapes sans savoir de quelle recette. */}
      {/*
        **Le héros de la fiche** (refonte éditoriale).

        Ce qu'il remplace : un titre de 24 px posé sur une page vide, un
        bouton « Cuisiner » seul sur sa ligne, et — quatre blocs plus bas,
        dans l'onglet « À propos » — une photo de 256 px de large montrée à
        côté de la description. Sur une page dont le sujet est un plat, la
        photographie de ce plat était le sixième élément rencontré, à la
        taille d'une vignette de contact, tandis que le premier était un
        panneau de réglage de visibilité haut de 220 px portant un bouton
        étiré sur 1740 px.

        L'ordre est inversé : la photo et le nom du plat d'abord, ensemble,
        puis ce qu'on peut en faire, puis seulement les réglages. Deux
        colonnes au-dessus de `lg` (texte 7/12, photo 5/12), empilées en
        dessous — photo d'abord sur téléphone, parce que c'est elle qui dit
        « c'est bien cette recette-là » avant même le titre.

        **La photo quitte l'onglet « À propos »** : sous `lg`, elle
        disparaissait dès que la fiche s'ouvrait sur « Instructions », donc
        pour toute recette déjà cuisinée. C'est le même défaut qu'US-58 a
        corrigé sur le bouton « Cuisiner » et sur le titre — la photo était
        restée en arrière.
      */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:items-center lg:gap-10">
        {detail.image_path !== null && (
          <div className="relative order-first lg:order-last lg:col-span-5">
            <img
              data-testid="recipe-photo"
              src={`/api/photos/${detail.image_path}`}
              alt=""
              // `aspect-[3/2]` : le format d'un appareil photo, donc celui de
              // la quasi-totalité des images reçues. En 4:3, chaque photo
              // importée perdait un huitième de sa hauteur au recadrage.
              // `shadow-raised` : une photo posée sur du papier porte une
              // ombre, un aplat de couleur n'en a pas besoin.
              className="aspect-[3/2] w-full rounded-card object-cover shadow-raised"
            />
            {/* Pastille de provenance (US-38) : les trois champs de la
                migration 004 étaient relégués en bas de fiche, en petit. */}
            {(detail.source_name ?? detail.author) !== null && (
              <p
                data-testid="recipe-source-badge"
                className="absolute left-3 top-3 max-w-[calc(100%-1.5rem)] truncate rounded-pill bg-surface/90 px-3 py-1.5 text-xs text-foreground shadow-card backdrop-blur"
              >
                {detail.source_url !== null ? (
                  <a href={detail.source_url} target="_blank" rel="noreferrer" className="underline">
                    {detail.source_name ?? detail.author}
                  </a>
                ) : (
                  detail.source_name ?? detail.author
                )}
              </p>
            )}
          </div>
        )}

        <div className={`flex flex-col gap-4 ${detail.image_path !== null ? 'lg:col-span-7' : 'lg:col-span-9'}`}>
        {/* `text-display` et non `text-heading-1` : le nom du plat est le seul
            endroit de l'application qui porte ce cran, et c'est ce qui fait
            qu'une fiche se lit comme une page de livre plutôt que comme un
            enregistrement. L'axe optique de Fraunces suit (index.css). */}
        <h1 className="text-display">{detail.title}</h1>
        {detail.description !== null && (
          <p className="max-w-reading text-lede">{detail.description}</p>
        )}
        {meta.length > 0 && (
          /* Les repères de service — durée, portions, nature, cuisine —
             séparés par des filets verticaux plutôt que par des points
             médians : à cette taille, une suite de « · » se lit comme du
             texte continu, et l'œil ne trouve plus la durée du premier coup.
             Chaque valeur devient une unité qu'on peut sauter. */
          <p data-testid="recipe-meta" className="flex flex-wrap items-center gap-x-3 gap-y-1 text-meta">
            {meta.map((part, index) => (
              <span key={part} className="flex items-center gap-3">
                {index > 0 && <span aria-hidden="true" className="h-3 w-px bg-border-strong" />}
                {part}
              </span>
            ))}
          </p>
        )}
        {/* « D'après le pot-au-feu d'Arthur » (US-49, affiché en US-65).
            `forked_from_title` était calculé, typé, testé côté serveur… et
            **jamais rendu** : la mention promise par le §2 de CLAUDE.md
            n'existait pas à l'écran. Le serveur le met déjà à `null` quand
            l'original n'est plus visible — il n'y a rien à filtrer ici. */}
        {detail.forked_from_title !== null && (
          <p data-testid="recipe-forked-from" className="text-sm text-foreground-secondary">
            D’après « {detail.forked_from_title} »
          </p>
        )}
        <AppartenanceAuxCollections recipeId={detail.id} />

        {/* **« Cuisiner » rejoint le héros** (refonte éditoriale). Il était
            seul sur sa ligne, entre le titre et la photo, séparé des deux par
            32 px de vide : l'action que la page existe pour déclencher
            n'appartenait visuellement à rien. Elle appartient au nom du plat,
            elle se place donc sous lui.

            Tout le reste est inchangé depuis US-58 : **monté une seule fois,
            déplacé par le CSS**. Barre fixe au-dessus de la navigation sur
            téléphone — `bottom-20`, qu'il ne doit jamais recouvrir —, dans le
            flux du héros sur grand écran. Deux instances masquées l'une à
            l'autre produiraient deux liens « Cuisiner » dans l'arbre
            d'accessibilité. Masqué pendant la cuisson, où le mode cuisine
            occupe déjà tout l'écran. */}
        {!cooking && (
          <div className={[
            'fixed inset-x-0 bottom-20 z-40 border-t border-border bg-surface p-3',
            'lg:static lg:z-auto lg:mt-2 lg:border-0 lg:bg-transparent lg:p-0',
          ].join(' ')}
          >
            <Link
              to={paths.cook(detail.id)}
              className={buttonClasses('primary', 'large', 'w-full justify-center lg:w-auto')}
            >
              Cuisiner
            </Link>
          </div>
        )}
        </div>
      </div>

      {/* **« Cuisiner » ne dépend plus de l'onglet actif** (US-58).

          Il vivait dans le bloc « À propos », donc masqué sous `lg` dès que la
          fiche s'ouvrait sur « Instructions » — c'est-à-dire pour toute recette
          déjà cuisinée, la totalité du corpus importé. Le bouton disparaissait
          **exactement pour les recettes qu'on connaît**, et il fallait revenir
          sur « À propos » pour le retrouver : trois gestes au lieu de deux,
          ajoutés par une optimisation censée en économiser un.

          **Monté une seule fois, déplacé par le CSS** — comme la navigation
          d'US-56. Deux instances masquées l'une à l'autre produiraient deux
          liens « Cuisiner » dans l'arbre d'accessibilité.

          `bottom-20` sur mobile : au-dessus de la navigation principale, qu'il
          ne doit jamais recouvrir. Masqué pendant la cuisson, où le mode
          cuisine occupe déjà tout l'écran. */}

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(16rem,22rem)_1fr]">
        <RecipeTabs
          ouverture={ongletDOuverture({ cook_logs: detail.cook_logs, cooked_before: detail.cooked_before })}
          // Photo et description ont rejoint le héros : cet en-tête ne porte
          // plus que ce qui **agit** sur la recette — les gestes, les
          // réglages, la mémoire.
          apropos={(
      <header className="flex flex-col gap-6 lg:col-span-2">
        <div className="flex min-w-0 flex-col gap-4">
          {/* La bascule de visibilité (US-49) descend **sous** le héros. Elle
              ouvrait la fiche : un panneau de 220 px expliquant qui peut voir
              la recette, avant même son nom. C'est une décision sur la
              recette, elle mérite sa place — pas la première. */}
          {modifiable && <VisibilityToggle detail={detail} onChanged={() => { setRefreshToken((n) => n + 1); }} />}

          {/* Trois rangs, là où six contrôles se valaient (refonte
              éditoriale) : la seule action que l'on vient faire, celles qu'on
              fait parfois, celles de service. Elles étaient alignées en une
              rangée de deux rectangles cerclés et quatre liens soulignés,
              dans un ordre qui ne disait rien. */}
          <nav aria-label="Actions sur la recette" className="flex flex-wrap items-center gap-2">
            {/* **Cuisiner et tenir son journal restent ouverts** sur la
                recette partagée d'un autre : c'est tout l'objet du livre
                familial. Modifier, ranger et archiver disparaissent — la
                recette appartient à quelqu'un, le geste prévu est la copie
                (US-49, cas 02). Ces boutons répondaient 404 depuis le
                correctif d'US-46 ; les afficher revenait à promettre puis à
                ne rien faire. */}
            <Button type="button" variant="secondary" onClick={() => { setAddingCookLog(true); }}>
              Ajouter une réalisation
            </Button>
            {/* Statut de rangement (US-21) : « à cuisiner » et « favorite »
                sont des intentions déclarées ici, sur la fiche, au moment où
                on la lit — c'est là qu'on sait si on veut la refaire.

                **Hors du bloc « modifiable » depuis US-51.** Le correctif
                d'US-46 les y avait mis, avec un raisonnement juste à l'époque :
                le statut vivait sur la recette, ranger celle d'un autre
                changeait sa fiche à lui. Il vit maintenant dans
                `recipe_member_status` : mettre la Tropézienne de Claire en
                favorite ne touche à rien chez Claire. C'était la limite que le
                correctif rendait visible, et c'est elle que cette story lève. */}
            {rangeable && (
              <>
                <Button
                  type="button"
                  variant={detail.status === 'a_cuisiner' ? 'secondary' : 'quiet'}
                  size="compact"
                  aria-pressed={detail.status === 'a_cuisiner'}
                  disabled={busy}
                  onClick={() => { toggleStatus(detail, 'a_cuisiner'); }}
                >
                  À cuisiner
                </Button>
                <Button
                  type="button"
                  variant={detail.status === 'favorite' ? 'secondary' : 'quiet'}
                  size="compact"
                  aria-pressed={detail.status === 'favorite'}
                  disabled={busy}
                  onClick={() => { toggleStatus(detail, 'favorite'); }}
                >
                  Favori
                </Button>
              </>
            )}
            {modifiable && (
              <>
                <Link to={paths.editRecipe(detail.id)} className={buttonClasses('quiet', 'compact')}>
                  Modifier
                </Link>
                <Button type="button" variant="quiet" size="compact" disabled={busy} onClick={() => { toggleArchive(detail); }}>
                  {detail.archived === 1 ? 'Restaurer' : 'Archiver'}
                </Button>
              </>
            )}
            {/* Partage (US-33) : un seul bouton, trois formes derrière — le
                texte pour une messagerie, le lien pour la vraie fiche, le PDF
                pour le placard. L'export PDF isolé y a été rangé.

                **Distinct de la visibilité** (US-49) : celui-ci envoie la
                recette à quelqu'un qui n'a pas de compte, celle-là décide qui
                la voit dans la famille. Deux mécanismes indépendants — les
                confondre dans un même bouton serait la faute la plus facile
                à commettre ici, d'où le libellé « Envoyer un lien ». */}
            {/* Envoyer un lien public, c'est publier hors du foyer : ce geste
                appartient au propriétaire de la recette, pas à qui la lit. */}
            {modifiable && (
              <Button type="button" variant="quiet" size="compact" onClick={() => { setSharing((open) => !open); }}>
                Envoyer un lien
              </Button>
            )}
          </nav>

          <MemoryStrip
            cookLogs={detail.cook_logs}
            now={now}
            recipeId={detail.id}
            memberId={session?.member?.id ?? null}
          />
        </div>
      </header>
          )}
          ingredients={(
        <div className="lg:sticky lg:top-6 lg:self-start">
          <IngredientsPanel
            recipeId={detail.id}
            referenceServings={detail.servings}
            servingsLabel={detail.servings_label}
            ingredients={detail.ingredients}
            system={system}
          />
        </div>
          )}
          instructions={(
        <section aria-labelledby="titre-etapes" className="flex flex-col gap-4">
          <h2 id="titre-etapes" className="text-heading-2">Étapes</h2>
          {detail.steps.length === 0 ? (
            <ProcedeAbsent recipe={detail} modifiable={modifiable} />
          ) : (
            <StepList steps={detail.steps} ingredientsDeLEtape={ingredientsDeLEtape} />
          )}
        </section>
          )}
        />
      </div>

      {/* **Ma note, et celle de personne d'autre** (US-64).
          Elle vivait dans `recipe.notes`, colonne de la recette : sur une
          recette partagée, la note de son propriétaire était lue par toute la
          famille, alors que l'écran de bascule promettait l'inverse. Elle vit
          maintenant dans une table par membre, et s'écrit **depuis la fiche en
          lecture** — parce que quiconque voit la recette peut l'annoter, y
          compris sur la recette de quelqu'un d'autre, ce que l'écran
          d'édition (réservé au propriétaire) ne permettrait pas.

          Repliée quand elle est vide, comme le rangement : US-14 pose que la
          fiche en lecture ne porte aucun champ de saisie au repos. */}
      {rangeable && (
        detail.notes === null ? (
          <CollapsiblePanel label="Ajouter une note pour moi" sectionLabel="Ma note">
            <div className="pt-3">
              <NoteEditor note="" busy={busy} onSave={(valeur) => saveNote(detail, valeur)} />
            </div>
          </CollapsiblePanel>
        ) : (
          <section aria-labelledby="titre-notes" className={cardClasses({ className: 'flex flex-col gap-2' })}>
            <h2 id="titre-notes" className="text-heading-2">Ma note</h2>
            <p className="text-meta">Personne d’autre ne la voit.</p>
            <NoteEditor note={detail.notes} busy={busy} onSave={(valeur) => saveNote(detail, valeur)} />
          </section>
        )
      )}

      {/* Étiquettes personnelles (US-51), sur la fiche en lecture et non plus
          seulement sur l'écran d'édition : ranger la recette d'un autre est
          désormais possible, et l'endroit où on décide qu'une recette est
          « pour les dimanches » est celui où on vient de la lire.

          **Repliée par défaut, et pas par timidité d'affichage.** US-14 pose
          que la fiche en lecture ne contient aucun champ de saisie — c'est ce
          qui la distingue du formulaire qu'elle a remplacé, et un test l'exige
          en comptant les `<input>` de l'écran. Le panneau replié tient les
          deux : au repos la fiche se lit, et ranger reste un geste explicite,
          à un clic. */}
      {rangeable && (
        <CollapsiblePanel label="Ranger cette recette" sectionLabel="Étiquettes personnelles">
          <div className="pt-3">
            <TagsEditor tags={detail.tags} onChange={(names) => saveTags(detail, names)} />
          </div>
        </CollapsiblePanel>
      )}

      {/* Propositions de modification (US-67) : le geste de convergence, en
          face de la copie qui est celui de divergence. Sur une recette privée,
          il n'y a personne à qui proposer — le panneau ne s'affiche que sur
          celles du livre de famille. */}
      {rangeable && detail.visibility === 'shared' && (
        <SuggestionPanel
          detail={detail}
          proprietaire={modifiable}
          onApplied={() => { setRefreshToken((n) => n + 1); }}
        />
      )}

      <RealisationsPanel
        cookLogs={detail.cook_logs}
        now={now}
        memberId={session?.member?.id ?? null}
        onPartage={detail.visibility === 'shared' && rangeable ? (log, shared) => { partagerRealisation(detail, log, shared); } : null}
      />

      {/* La suppression définitive (US-83), **tout en bas et seulement ici** :
          sur une recette déjà archivée, et pour le seul `proprietaire`. C'est
          le seul endroit de l'application où une donnée se perd, et sa place
          dans la page le dit — après tout ce qu'on vient lire, jamais à côté
          d'« Archiver ».

          `supprimable` et non `modifiable` : le rôle s'ajoute à la propriété,
          il ne la remplace pas. Un membre ordinaire ne voit pas ce panneau
          grisé, il ne le voit pas — même famille que « 404, jamais 403 ». */}
      {supprimable && <SuppressionDefinitive recipeId={detail.id} title={detail.title} />}

      {/* Trois informations distinctes depuis US-27 : d'où vient la recette,
          qui l'a écrite, et le lien. Elles étaient confondues, si bien que le
          champ « Source » affichait un pseudonyme. */}
      {(detail.source_name !== null || detail.source_url !== null || detail.author !== null) && (
        <p className="text-sm text-foreground-muted">
          {detail.author !== null && <>De {detail.author}</>}
          {detail.author !== null && (detail.source_name !== null || detail.source_url !== null) && ' · '}
          {(detail.source_name !== null || detail.source_url !== null) && (
            <>
              Source :{' '}
              {detail.source_url !== null ? (
                <a href={detail.source_url} target="_blank" rel="noreferrer" className="underline">
                  {detail.source_name ?? detail.source_url}
                </a>
              ) : (
                detail.source_name
              )}
            </>
          )}
        </p>
      )}

      {cooking && (
        <CookMode
          detail={detail}
          onExit={() => { onCookingChange(false); }}
          onCookLogSaved={(updated) => { setDetail(updated); }}
        />
      )}
    </article>
  );
}
